import { z } from "zod";

import { SenderRecipientOption } from "../components/SenderRecipientDropdown";

export interface AiSender {
	userId?: number;
	emailId?: number;
}

/**
 * Pick the sender option that matches the AI generation sender for the given
 * channel. Prefers an exact (userId, emailId) match for email items, falling
 * back to any email option for the user. LinkedIn items match by userId only.
 *
 * Returns `undefined` when there is no AI sender or no matching option — the
 * caller is expected to fall back to its existing sender resolution logic.
 */
export const pickAiSenderOption = (
	allOptions: SenderRecipientOption[],
	channel: "email" | "linkedin",
	aiSender: AiSender | undefined
): SenderRecipientOption | undefined => {
	if (!aiSender?.userId) {
		return undefined;
	}

	// A disabled option cannot send (a mailbox that needs a reconnect): never put it on a draft.
	const options = allOptions.filter((o) => !o.disabled);

	if (channel === "linkedin") {
		return options.find((o) => o.type === "linkedin" && o.userId === aiSender.userId);
	}

	const exactEmail = options.find(
		(o) =>
			o.type === "email" &&
			o.userId === aiSender.userId &&
			(aiSender.emailId === undefined || o.emailId === aiSender.emailId)
	);

	if (exactEmail) {
		return exactEmail;
	}

	return options.find((o) => o.type === "email" && o.userId === aiSender.userId);
};

/**
 * The sender an AI draft on a thread takes: the AI generation's, unless that is another mailbox of
 * the thread's own sender — the generation only knows the sender's primary mailbox, and a reply
 * stays on the thread's (PREC-4312, E4). Without a thread sender, the generation's.
 */
export const preferThreadSender = (
	aiPicked: SenderRecipientOption | undefined,
	threadSender: SenderRecipientOption | undefined
): SenderRecipientOption | undefined =>
	threadSender && aiPicked?.userId === threadSender.userId ? threadSender : aiPicked;

const AiSenderContextSchema = z.object({
	generation_config: z.object({
		sender: z.object({
			userId: z.number().optional(),
			emailId: z.number().optional(),
		}),
	}),
});

/**
 * Safely extracts the AI generation sender (userId / emailId) from the
 * `contextPayload` returned by the copywriting orchestration API.
 *
 * Returns an empty object when the payload is missing, malformed or from an
 * older shape that does not include the sender info — callers can then fall
 * back to their existing default-resolution logic.
 */
export const resolveAiSenderUserId = (contextPayload: unknown): AiSender => {
	const parsed = AiSenderContextSchema.safeParse(contextPayload);

	if (!parsed.success) {
		return {};
	}

	const { userId, emailId } = parsed.data.generation_config.sender;

	return { userId, emailId };
};
